/**
 * InHeatDialog — Ted 09-29 (#1111 AC6–7, lane LC). The mid-stay action.
 *
 * Ted: "If a female dog goes into heat unexpectedly after she has already
 * begun a boarding stay, she would need to be immediately isolated and the
 * owner or emergency contact contacted to arrange pickup. That boarding stay
 * would be terminated rather than allowing her to remain in the general
 * facility, unless pick up is impossible."
 *
 * Two phases against ONE idempotent route (`markStayInHeat`):
 *   1. CONFIRM → "Mark in heat — isolate now". The stay is isolated the
 *      moment the server answers (the ISOLATED pill draws everywhere the
 *      registry draws) and an Incident is filed. Closing the dialog after
 *      this changes nothing — she is already isolated.
 *   2. CONTACTS → the owner's and the emergency contact's numbers, two
 *      checkboxes ("Owner contacted" / "Emergency contact contacted"), and
 *      the two ways out: "Pickup arranged → go to checkout" (the EXISTING
 *      checkout; no money logic here — Ted's refund answer is pending) or
 *      "Pickup impossible → stays isolated" (the stay stays open).
 *
 * Offered only for a dog the registry's heat rule applies to, on a
 * checked-in stay (the surface decides with `heatCheckAppliesFromCard`).
 */
import { useEffect, useRef, useState } from 'react';
import { markStayInHeat, type InHeatContact, type InHeatOutcome, type InHeatResponse } from '../../lib/api.js';
// Sawyer 12 incident-wording-pet-notes (F3) — the owner's word for the log.
import { useTenantVocabulary } from '../../lib/useTenantVocabulary.js';
import './CheckInInterrupt.css';

export interface InHeatRequest {
  bookingId: string;
  dog: { dogId: string; name: string };
  customerName: string;
  /** Runs after phase 1 succeeds (the row should re-read its flags). */
  onMarked?: (res: InHeatResponse) => void;
  /** Runs after "Pickup arranged" — the surface opens the existing checkout. */
  onPickupArranged: (bookingId: string) => void;
  onClose: () => void;
}

interface InHeatDialogProps {
  request: InHeatRequest;
  apiUrl: string;
  token: string;
}

type Phase = { kind: 'confirm' } | { kind: 'contacts'; res: InHeatResponse } | { kind: 'done' };

function Contact({ role, contact, testId }: { role: string; contact: InHeatContact | null; testId: string }): JSX.Element {
  return (
    <li className="ferro-in-heat__contact" data-testid={testId}>
      <span className="ferro-in-heat__contact-role">{role}</span>
      {contact ? (
        <>
          <span>{contact.name ?? 'Name not on file'}{contact.relationship ? ` (${contact.relationship})` : ''}</span>
          <a className="ferro-in-heat__phone" href={`tel:${contact.phone.replace(/[^+\d]/g, '')}`}>
            {contact.phone}
          </a>
          {contact.homePhone ? <span>Home: {contact.homePhone}</span> : null}
        </>
      ) : (
        <span>No {role.toLowerCase()} on file.</span>
      )}
    </li>
  );
}

export function InHeatDialog({ request, apiUrl, token }: InHeatDialogProps): JSX.Element {
  const { incident: noun } = useTenantVocabulary(apiUrl);
  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerContacted, setOwnerContacted] = useState(false);
  const [emergencyContacted, setEmergencyContacted] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, [phase.kind]);

  const mark = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await markStayInHeat(apiUrl, token, request.dog.dogId, { bookingId: request.bookingId });
      request.onMarked?.(res);
      setPhase({ kind: 'contacts', res });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark the stay in heat — try again');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (outcome: InHeatOutcome, res: InHeatResponse) => {
    setBusy(true);
    setError(null);
    try {
      await markStayInHeat(apiUrl, token, request.dog.dogId, {
        bookingId: request.bookingId,
        ownerContacted,
        emergencyContactContacted: emergencyContacted,
        outcome,
        ...(res.incident ? { incidentId: res.incident.id } : {}),
      });
      setPhase({ kind: 'done' });
      if (outcome === 'pickup_arranged') request.onPickupArranged(request.bookingId);
      else request.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the contact — try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="in-heat-title"
      data-testid="in-heat-dialog"
      data-booking-id={request.bookingId}
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || busy) return;
        event.stopPropagation();
        request.onClose();
      }}
    >
      <div className="ferro-blocking-dialog__panel ferro-checkin-interrupt">
        <p className="ferro-checkin-interrupt__eyebrow ferro-checkin-interrupt__eyebrow--danger">In heat</p>
        {phase.kind === 'confirm' ? (
          <>
            <h2 id="in-heat-title">Mark {request.dog.name} in heat?</h2>
            <p className="ferro-checkin-interrupt__lead">
              She is isolated from the floor the moment you confirm — no group time, no shared run — and {noun.aOne} is
              filed. Next you get {request.customerName}&rsquo;s numbers to arrange pickup.
            </p>
            {error ? <p className="ferro-in-heat__error" role="alert">{error}</p> : null}
            <div className="ferro-blocking-dialog__actions">
              <button type="button" className="ferro-btn ferro-btn--ghost" ref={cancelRef} onClick={request.onClose} disabled={busy} data-testid="in-heat-cancel">
                Cancel
              </button>
              <button type="button" className="ferro-btn ferro-btn--primary" onClick={() => void mark()} disabled={busy} data-testid="in-heat-mark">
                {busy ? '…' : 'Mark in heat — isolate now'}
              </button>
            </div>
          </>
        ) : phase.kind === 'contacts' ? (
          <>
            <h2 id="in-heat-title">{request.dog.name} is isolated — arrange pickup</h2>
            <p className="ferro-checkin-interrupt__lead" data-testid="in-heat-marked">
              Isolated{phase.res.incident ? `, ${noun.oneLower} filed` : ''}. Call the owner first, then the emergency contact.
            </p>
            <ul className="ferro-in-heat__contacts">
              <Contact role="Owner" contact={phase.res.contacts.owner} testId="in-heat-contact-owner" />
              <li className="ferro-in-heat__contact">
                <label htmlFor="in-heat-owner-contacted">
                  <input
                    type="checkbox"
                    id="in-heat-owner-contacted"
                    name="in-heat-owner-contacted"
                    checked={ownerContacted}
                    onChange={(e) => setOwnerContacted(e.target.checked)}
                    data-testid="in-heat-owner-contacted"
                  />
                  Owner contacted
                </label>
              </li>
              <Contact role="Emergency contact" contact={phase.res.contacts.emergency} testId="in-heat-contact-emergency" />
              <li className="ferro-in-heat__contact">
                <label htmlFor="in-heat-emergency-contacted">
                  <input
                    type="checkbox"
                    id="in-heat-emergency-contacted"
                    name="in-heat-emergency-contacted"
                    checked={emergencyContacted}
                    onChange={(e) => setEmergencyContacted(e.target.checked)}
                    data-testid="in-heat-emergency-contacted"
                  />
                  Emergency contact contacted
                </label>
              </li>
            </ul>
            {error ? <p className="ferro-in-heat__error" role="alert">{error}</p> : null}
            <div className="ferro-blocking-dialog__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                ref={cancelRef}
                onClick={() => void finish('pickup_impossible', phase.res)}
                disabled={busy}
                data-testid="in-heat-pickup-impossible"
              >
                Pickup impossible — stays isolated
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={() => void finish('pickup_arranged', phase.res)}
                disabled={busy}
                data-testid="in-heat-pickup-arranged"
              >
                Pickup arranged — go to checkout
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export interface InHeatAction {
  open: (request: InHeatRequest) => void;
  dialog: JSX.Element | null;
}

/** One dialog at a time; the surface renders `dialog` once. The token is read when the dialog mounts. */
export function useInHeatAction(apiUrl: string, getToken: () => string | null | undefined): InHeatAction {
  const [current, setCurrent] = useState<InHeatRequest | null>(null);
  const open = (request: InHeatRequest) => setCurrent(request);
  const token = current ? (getToken() ?? null) : null;
  const dialog =
    current && token ? (
      <InHeatDialog
        key={current.bookingId}
        apiUrl={apiUrl}
        token={token}
        request={{
          ...current,
          onClose: () => {
            setCurrent(null);
            current.onClose();
          },
          onPickupArranged: (id) => {
            setCurrent(null);
            current.onPickupArranged(id);
          },
        }}
      />
    ) : null;
  return { open, dialog };
}
